import { assertVal } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Communicator } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui/Colors';
import { Camera } from '../../canvas/Camera';
import { ConfettiCannon, ConfettiRound } from '../../canvas/ConfettiCannon';
import { Telemetry } from '../../canvas/Telemetry';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { ToolOverlay } from '../../canvas/tooling/ToolOverlay';
import { resolve } from '../../Container';

type DragRegion = { from: Point, to: Point };

// const confettiTip = HelpTip(
//     'annotate',
//     'videos',
//     'The Confetti Pen',
//     'We\'ve recommended a tutorial video for you that covers the Vanishing Pen!'
// );

export class ConfettiToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(Communicator) private comms!: Communicator;
    @resolve(ConfettiCannon) private cannon!: ConfettiCannon;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;
    @resolve(Telemetry) private telemetry!: Telemetry;

    private region = null as DragRegion | null;

    public init() {
        this.burden(this.overlay.add(this.doPaint));
        this.burden(() => void (this.telemetry.flush()));
        // this.dispatch(haugOpenVideo, ['Confetti']);
    }

    public getPreferredCursor(state: string) {
        if (state === 'idle' || state === 'confetting') {
            return 'crosshair';
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (state === 'idle' && mi.button === MouseButtons.Main) {
            const mpt = this.camera.toModelPoint(mi.position);
            this.region = { from: mpt, to: mpt };
            return 'confetting';
        }
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        if (state === 'confetting') {
            assertVal(this.region, 'ConfettiToolBehavior: creating without region?');
            this.region.to = this.camera.toModelPoint(mi.position);
            this.overlay.repaint();
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state === 'confetting') {
            assertVal(this.region, 'ConfettiToolBehavior: creating without region?');

            this.region.to = this.camera.toModelPoint(mi.position);
            const { from, to } = this.region;

            const dist = Math.abs(from.subtract(to).length());
            if (dist >= 5) {
                this.cannon.fire([null, from, to]);
                this.comms.send('confetti', [from, to]);
                this.telemetry.increment('confetti', ConfettiRound.computeCount(from, to));
            }

            this.region = null;
            this.overlay.repaint();
            return 'idle';
        }
    }

    private doPaint = (gfx: UniversalSurfaceRenderer) => {
        const { modelViewMatrix: mv } = this.camera;
        const { from, to } = this.region ?? {};
        if (!from || !to) return;

        const a = mv.apply(from);
        const b = mv.apply(to);

        gfx.strokeStyle = Colors.primaryDark;
        gfx.lineWidth = 2;

        gfx.setLineDash([4, 4]);
        gfx.strokeLine(Point.flatten([a, b]));
        gfx.setLineDash([]);
        gfx.strokeCircle(a.x, a.y, Math.max(8, ConfettiRound.computeVelocity(a, b) * .8));
    };
}


export const confettiTool = defineToolWithCommonBehaviors({
    id: 'confettiTool',
    label: 'Confetti',
    category: 'gestures',
    icon: 'deqo://icons/d2/confetti',
    weight: 4030,
    shortcut: '3',
    behaviors: [
        ConfettiToolBehavior,
    ]
});
